import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig, YAxisConfig} from '../types';

/** Approximate seconds per granularity interval. */
const GRANULARITY_SECONDS: Partial<Record<DataGranularity, number>> = {
  H: 3600,
  '3H': 10800,
  '6H': 21600,
  D: 86400,
  W: 604800,
  M: 2592000,
};

export interface LightningStrikeCountChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
}

export const lightningStrikeCountChart = (
  options: LightningStrikeCountChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
  } = options;

  const hasComparison = !!comparisonData;
  const hasDistance = data.some(
    (p) => p.LIGHTNING_STRIKE_DISTANCE != null,
  );
  const hasDistanceRange = data.some(
    (p) => p.LIGHTNING_STRIKE_DISTANCE_RANGE != null,
  );
  const distanceUnitLabel = Units.getDistanceUnitLabel(units);

  const series: SeriesConfig[] = [
    {
      id: 'lightning',
      type: 'bar',
      dataKey: 'LIGHTNING_STRIKE_COUNT',
      name: 'Lightning',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: false,
      barRadius: [theme.borderRadius, theme.borderRadius, 0, 0],
      zIndex: 1,
      ...(hasComparison ? {barGroupIndex: 0, barGroupCount: 2} : {}),
    },
  ];

  if (hasDistance) {
    if (hasDistanceRange) {
      series.push({
        id: 'lightning-distance-range',
        type: 'areaRange',
        dataKey: 'LIGHTNING_STRIKE_DISTANCE_RANGE',
        rangeKey: 'LIGHTNING_STRIKE_DISTANCE_RANGE',
        name: 'Min / Max',
        color: theme.colors.secondary,
        yAxisIndex: 1,
        interpolation: 'monotone',
        fillOpacity: 0.15,
        lineWidth: 0,
        showMarkers: false,
        zIndex: 0,
      });
    }

    series.push({
      id: 'lightning-distance',
      type: 'line',
      dataKey: 'LIGHTNING_STRIKE_DISTANCE',
      name: `Distance (${distanceUnitLabel})`,
      color: theme.colors.secondary,
      yAxisIndex: 1,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: hasDistanceRange ? 1 : 0,
    });
  }

  if (hasComparison) {
    series.push({
      id: 'lightning-comparison',
      type: 'bar',
      dataKey: 'LIGHTNING_STRIKE_COUNT_COMP',
      name: comparisonLabel ?? 'Comparison',
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'linear',
      showMarkers: false,
      barRadius: [theme.borderRadius, theme.borderRadius, 0, 0],
      opacity: 0.5,
      zIndex: 0,
      barGroupIndex: 1,
      barGroupCount: 2,
    });
  }

  // Bar offset: center bars between tick marks
  const granSeconds = GRANULARITY_SECONDS[granularity] ?? 0;
  const barOffset = granSeconds / 2;
  const offsetData =
    barOffset > 0
      ? data.map(p => ({...p, timestamp: p.timestamp + barOffset}))
      : data;

  const customFormatters: Record<string, (v: number) => string> = {
    LIGHTNING_STRIKE_COUNT: (v: number) => `${Math.round(v)}`,
    LIGHTNING_STRIKE_COUNT_COMP: (v: number) => `${Math.round(v)}`,
  };
  if (hasDistance) {
    customFormatters.LIGHTNING_STRIKE_DISTANCE = (v: number) =>
      `${v.toFixed(1)} ${distanceUnitLabel}`;
  }

  const tooltipConfig = createTooltipConfig({
    timezone,
    granularity,
    metric: 'LIGHTNING_STRIKE_COUNT',
    units,
    theme,
    dateTimeConfig,
    customFormatters,
  });

  const yAxes: YAxisConfig[] = [
    {
      ...createYAxisConfig({
        id: 'lightning',
        theme,
        domain: [0, 'auto'],
        precision: 0,
      }),
      tickFormat: (value: number) => {
        if (value < 0) return '';
        if (Math.abs(value - Math.round(value)) > 0.01) return '';
        return `${Math.max(0, Math.round(value))}`;
      },
    },
  ];

  if (hasDistance) {
    yAxes.push(
      createYAxisConfig({
        id: 'distance',
        unitLabel: distanceUnitLabel,
        theme,
        position: 'right',
        domain: [0, 'auto'],
        gridLines: false,
      }),
    );
  }

  return {
    height,
    data: offsetData,
    series,
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity,
      theme,
    }),
    yAxes,
    tooltip: {
      ...tooltipConfig,
      formatTimestamp:
        barOffset > 0
          ? (ts: number) => tooltipConfig.formatTimestamp(ts - barOffset)
          : tooltipConfig.formatTimestamp,
    },
    legend: createLegendConfig(hasDistance || series.length > 1, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
